import { Routes, Route, Navigate, useParams } from "react-router-dom";
import Layout from "../layouts/Layout";
import Login from "../pages/Login";
import Landing from "../pages/Landing";
import UserManagement from "../pages/UserManagement";
import Cases from "../pages/Cases";
import CaseDetails from "../pages/CaseDetails";
import Analytics from "../pages/Analytics";
import AnalyticsDashboard from "../analytics/AnalyticsDashboard";
import BreakinPage from "../breakin/BreakinPage";
import { BREAKIN_DEPLOYMENT } from "../breakin/breakinApi";
import PaydPage from "../payd/PaydPage";
import PeopleRedesign from "../redesign/PeopleRedesign";
import CaseDetailsRedesign from "../redesign/CaseDetailsRedesign";
import PropertyCaseDetails from "../property/PropertyCaseDetails";
import { getClientEntry } from "../config/clientConfig";
import { getSourceName } from "../services/authService";
import CasesRedesign from "../redesign/CasesRedesign";
import PropertyCases from "../pages/PropertyCases";
import PropertyCaseDetailsPage from "../pages/PropertyCaseDetails";
import PropertyAnalytics from "../pages/PropertyAnalytics";
import PropertyCasesRedesign from "../redesign/PropertyCasesRedesign";
import PropertyCaseDetailsRedesign from "../redesign/PropertyCaseDetailsRedesign";
import ProtectedRoute from "../components/ProtectedRoute";
import { usePortalConfig } from "../context/PortalConfigContext";
import { isAuthenticated } from "../services/authService";

/**
 * The redesign first shipped at /cases/:caseId/redesign, before it grew to cover
 * the list as well and moved under /redesign/*. Kept as a redirect so links from
 * that window still work instead of hitting the 404 catch-all.
 */
function LegacyRedesignRedirect() {
  const { caseId } = useParams();
  return <Navigate to={`/redesign/cases/${caseId}`} replace />;
}

/**
 * A property claim needs a different page, not a different mode.
 *
 * The motor detail page is panels, damage intensity per panel and a 3D vehicle;
 * a building has none of those, and its claim is photographs plus the
 * coordinates they were taken at. Which page to render follows from the
 * client's own file — `inspectionLine` — so both designs' routes dispatch the
 * same way and no page has to know about the other.
 */
function CaseDetailsByLine({ design = "current" }) {
  const line = getClientEntry(getSourceName())?.inspectionLine;
  if (line === "property") return <PropertyCaseDetails />;
  return design === "redesign" ? <CaseDetailsRedesign /> : <CaseDetails />;
}

/**
 * The same dispatch, for the list.
 *
 * Only the detail route used to do this, so choosing Property on the
 * post-login picker pointed the session at the property client and then
 * rendered the MOTOR list — which queried the property backend and looked, to
 * the reader, like Motor had opened. The two lines differ in their columns and
 * their thumbnails, not just in their case pages, so the list has to dispatch
 * too or the picker silently does nothing.
 */
function CasesByLine({ design = "current" }) {
  const line = getClientEntry(getSourceName())?.inspectionLine;
  if (line === "property") {
    return design === "redesign" ? (
      <PropertyCasesRedesign detailsBasePath="/redesign/property-cases" />
    ) : (
      <PropertyCases />
    );
  }
  return design === "redesign" ? (
    <CasesRedesign detailsBasePath="/redesign/cases" />
  ) : (
    <Cases />
  );
}

/**
 * Analytics is real for a client whose backend serves it, and demo data for
 * everyone else.
 *
 * GET /get_analytics_data answers on both Ergo hosts. Bajaj and Hegic were not
 * probed for it, and the existing page renders invented figures from
 * src/data/claimsData.js — which is exactly what should not appear under a
 * client's name. So their nav item stays disabled, and this only reaches the
 * old page if someone types the URL.
 */
function AnalyticsByClient() {
  const client = getClientEntry(getSourceName());
  return client?.hasAnalytics ? <AnalyticsDashboard /> : <Analytics />;
}

function NotFound() {
  return (
    <div style={{ padding: 32, textAlign: "center" }}>404 - Page Not Found</div>
  );
}

// Phase 3: Full-screen loading overlay shown while portal config is being
// fetched for an authenticated session (first login or stale cache).
function ConfigLoadingScreen() {
  return (
    <div className="fixed inset-0 bg-background-dark flex flex-col items-center justify-center z-50">
      <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin mb-4" />
      <p className="text-white text-sm font-noto-sans opacity-70">
        Loading portal configuration…
      </p>
    </div>
  );
}

export default function AppRoutes() {
  const { configLoading } = usePortalConfig();

  // Block the dashboard from rendering if we're mid-fetch and the user is
  // authenticated. Public routes (login, landing) are always accessible.
  const showConfigLoading = configLoading && isAuthenticated();

  return (
    <>
      {showConfigLoading && <ConfigLoadingScreen />}
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />

        {/* Protected routes with layout */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/people" element={<UserManagement />} />
          <Route path="/cases" element={<CasesByLine />} />
          <Route path="/cases/:caseId" element={<CaseDetailsByLine />} />

        {/* Side-by-side design comparison — the Clearline redesign covers both
            the list and the detail page, so it gets its own /redesign namespace
            rather than a /cases/:caseId/redesign suffix. A suffix would have put
            "/cases/redesign" (the list) in competition with "/cases/:caseId",
            which only works by React Router's static-beats-dynamic ranking.
            detailsBasePath keeps the redesigned list linking to the redesigned
            detail. */}
        <Route
          path="/cases/:caseId/redesign"
          element={<LegacyRedesignRedirect />}
        />
        <Route path="/redesign/people" element={<PeopleRedesign />} />
        <Route
          path="/redesign/cases"
          element={<CasesByLine design="redesign" />}
        />
        <Route
          path="/redesign/cases/:caseId"
          element={<CaseDetailsByLine design="redesign" />}
        />
          <Route path="/cases-modern/:caseId" element={<CaseDetails />} />
          <Route path="/analytics" element={<AnalyticsByClient />} />
          <Route path="/redesign/analytics" element={<AnalyticsDashboard />} />

        {/* Property case-review flow — a sibling to Motor's routes above, not
            a branch inside them. Reached from the post-login Motor/Property
            picker in Login.js; see propertyApiService.js for why it needs its
            own API surface rather than reusing apiService.js's functions
            directly. */}
        <Route path="/property-cases" element={<PropertyCases />} />
        <Route path="/property-analytics" element={<PropertyAnalytics />} />
        <Route path="/property-cases/:caseId" element={<PropertyCaseDetailsPage />} />
        <Route
          path="/redesign/property-cases"
          element={<PropertyCasesRedesign detailsBasePath="/redesign/property-cases" />}
        />
        <Route
          path="/redesign/property-cases/:caseId"
          element={<PropertyCaseDetailsRedesign />}
        />

        {/* Hegic modules reachable from the post-login picker. Break-in and PAYD
            are separate deployments; these routes exist so the picker does not
            dead-end on the 404 page, and state what each still needs. */}
        {/* Hegic's other two modules, both wired: break-in on its own API host
            with the same session token, PAYD on its portal with a cookie.

            They live under /redesign because that prefix is what Layout keys
            the light theme, the Clearline font and the new sidebar off. Both
            pages are written in that vocabulary, so outside the namespace they
            rendered light cards inside the old dark chrome. The bare paths
            stay as redirects: they are what the post-login picker used to
            point at, and what anyone's bookmark still says. */}
        <Route path="/redesign/breakin" element={<BreakinPage />} />
        {/* The claims page, pointed at break-in's backend: same contract,
            so the same page — without the two tabs that host has no routes
            for. */}
        <Route
          path="/redesign/breakin/:caseId"
          element={<CaseDetailsRedesign deployment={BREAKIN_DEPLOYMENT} />}
        />
        <Route path="/redesign/payd" element={<PaydPage />} />
        <Route path="/breakin" element={<Navigate to="/redesign/breakin" replace />} />
        <Route path="/payd" element={<Navigate to="/redesign/payd" replace />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}
